import { useContext, useMemo } from 'react';

import { ApiEndpoint } from '@/api/registry';
import { useSuspenseApiQuery } from '@/api/useApiQuery';
import type { EventBehaviorResponse, EventBehaviorTimeseriesPoint } from '@/features/forecasts/forecasts.api.types';
import { DemandForecastingPairContext } from '@/features/forecasts/providers/DemandForecastingPair';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { useTenant } from '@/providers/TenantProvider';
import { addDaysToIsoDate, isoYearsAgo, todayISO } from '@/utils/date';

export const ForecastEventType = {
  Orders: 'orders',
} as const;
export type ForecastEventType = (typeof ForecastEventType)[keyof typeof ForecastEventType];

export const ForecastDateType = {
  RequestDate: 'request_date',
} as const;
export type ForecastDateType = (typeof ForecastDateType)[keyof typeof ForecastDateType];

// Monday that begins the Monday–Sunday week containing the given date.
function weekStartMonday(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const day = date.getUTCDay(); // 0=Sun..6=Sat
  const daysFromMonday = (day + 6) % 7;
  date.setUTCDate(date.getUTCDate() - daysFromMonday);
  return date.toISOString().slice(0, 10);
}

function fillMissingWeeksTimeSeries(records: EventBehaviorTimeseriesPoint[]): EventBehaviorTimeseriesPoint[] {
  const result: EventBehaviorTimeseriesPoint[] = [];

  if (!records.length) return result;

  const sorted = records;
  const start = sorted[0].date;
  const end = sorted[sorted.length - 1].date;
  const byDate = new Map<string, EventBehaviorTimeseriesPoint>();

  for (const r of sorted) {
    byDate.set(r.date, r);
  }

  let cursor = start;

  while (cursor <= end) {
    const existing = byDate.get(cursor);

    result.push(
      existing ?? {
        date: cursor,
        order_sequence: null,
        quantity: 0,
        interarrival_days: null,
        day_of_week: null,
        order_minus_promise_days: null,
        order_minus_request_days: null,
        order_minus_ship_days: null,
      },
    );

    cursor = addDaysToIsoDate(cursor, 7);
  }

  return result;
}

function buildWeeklyActuals(records: EventBehaviorTimeseriesPoint[]): EventBehaviorTimeseriesPoint[] {
  if (!records.length) return [];

  const totals = new Map<string, number>();

  for (const record of records) {
    if (!record?.date || typeof record.date !== 'string') continue;
    const weekStart = weekStartMonday(record.date);
    const current = totals.get(weekStart) ?? 0;
    totals.set(weekStart, current + (record.quantity ?? 0));
  }

  const weekly = [...totals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, total]) => ({
      date,
      order_sequence: null,
      quantity: total,
      interarrival_days: null,
      day_of_week: null,
      order_minus_promise_days: null,
      order_minus_request_days: null,
      order_minus_ship_days: null,
    }));

  return fillMissingWeeksTimeSeries(weekly);
}

type UseOrderDataOptions = {
  startDate?: string;
  endDate?: string;
  primaryKey?: Record<string, string | undefined>;
  grain?: string[];
};

export function useOrderData(options?: UseOrderDataOptions) {
  const { tenantAlias, grains } = useTenant();
  const { tenantApiGrains, tenantApiFilters } = useTenantConfiguration();
  const pair = useContext(DemandForecastingPairContext);

  const { startDate = isoYearsAgo(2), endDate = todayISO(), primaryKey, grain } = options ?? {};

  const productField = grains.product ?? '';
  const customerField = grains.ship_to ?? '';
  const hasPairSelection = !!(pair?.productId || pair?.customerId);

  const resolvedPrimaryKey =
    primaryKey ??
    (hasPairSelection ? { [productField]: pair!.productId, [customerField]: pair!.customerId } : tenantApiFilters);
  const resolvedGrain = grain ?? (hasPairSelection ? [productField, customerField].filter(Boolean) : tenantApiGrains);

  const queryResult = useSuspenseApiQuery<EventBehaviorResponse>(tenantAlias, ApiEndpoint.EventBehavior, {
    body: {
      event: ForecastEventType.Orders,
      grain: resolvedGrain,
      primary_key: resolvedPrimaryKey,
      date_type: ForecastDateType.RequestDate,
      start_date: startDate,
      end_date: endDate,
    },
  });

  const response = queryResult?.data;
  const total = response?.metadata?.total_points ?? 0;
  const records = useMemo(() => response?.data ?? [], [response?.data]);
  const actuals = useMemo(() => buildWeeklyActuals(records), [records]);

  return { records, total, actuals };
}
